import { createSlice } from "@reduxjs/toolkit";
import axios from "../../utils/axios";

const initialState = {
    isLoading: false,
    error: null,
    allCartItems: {},
    cartItem: {},
    deleteStatus: false,
};

const accessToken = typeof window !== "undefined" ? localStorage.getItem("accessToken") : "";

const headers = {
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json'
};

const cartSlice = createSlice({
    name: "addToCart",
    initialState,
    reducers: {
        startLoading(state) {
            state.isLoading = true;
            state.error = null;
        },
        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },
        addToCartSuccess(state, action) {
            state.isLoading = false;
            state.cartItem = action.payload;
        },
        getAllCartItemsSuccess(state, action) {
            state.isLoading = false;
            state.allCartItems = action.payload;
        },

        deleteCartSuccess(state, action) {
            state.isLoading = false;
            state.deleteStatus = action.payload;
        },

        updateCartItemSuccess(state, action) {
            state.isLoading = false;
            const updatedItem = action.payload;
            if (state.allCartItems[updatedItem.item_id]) {
                state.allCartItems[updatedItem.item_id] = updatedItem;
            }
        },
    },
});

export const {
    startLoading,
    hasError,
    addToCartSuccess,
    getAllCartItemsSuccess,
    deleteCartSuccess,
    updateCartItemSuccess,
} = cartSlice.actions;

export default cartSlice.reducer;

export function getAllCartItems(customer_id, payload = { status: true }) {
    return async (dispatch) => {
        try {
            dispatch(startLoading());

            const response = await axios.get(`/cart/${customer_id}`, {
                params: payload,
                headers
            });

            const cartData = response?.data?.cart || [];
            dispatch(getAllCartItemsSuccess(cartData));

            return cartData; // ✅ This is what lets `.then(res => {...})` work

        } catch (error) {
            console.error("Error fetching cart items:", error);
            dispatch(hasError(error.message || "Error fetching cart items"));
            return []; // Return empty array to avoid crash
        }
    };
}


export function addCartItems(cartItem, toast, navigate, Buynow) {
  return async (dispatch) => {
    try {
      dispatch(startLoading());
      const response = await axios.post("/addtocart", cartItem, { headers });
      dispatch(addToCartSuccess(response?.data));
      localStorage.setItem("cart_id", response?.data?.cart_id);

      if (response?.data?.status === true) {
        const cart_id = localStorage?.getItem("cart_id") || null;
        const userData = JSON?.parse(localStorage?.getItem("user"));
        const customer_id = userData?.id || null;
        const token = localStorage?.getItem("accessToken") || null;

        if (token) {
          dispatch(getAllCartItems(customer_id, { status: true }));
        } else {
          dispatch(getAllCartItems(cart_id, { status: false }));
        }

        // ✅ Buy Now logic with contact check
        if (Buynow) {
          if (userData && userData.contact) {
            navigate("/checkout");
          } else {
            navigate("/cart");
          }

          window.scrollTo({ top: 0, behavior: "smooth" });
        }

        toast.success("Continue shopping or view your cart.");
        return { success: true };
      } else {
        toast.error("Ensure the item is available and try again later");
        return { success: false };
      }
    } catch (error) {
      console.error("Error adding item to cart", error);
      toast.error("Ensure the item is available and try again later.");
      dispatch(hasError(error.message || "Error adding item to cart"));
      return { success: false };
    }
  };
}


export const resendVerificationEmail = (email, toast) => {
  return async (dispatch) => {
    if (!email) {
      toast?.error("Email not found. Please login again.");
      return;
    }
    dispatch(resendVerificationEmail());

    try {
      const response = await axios.post('/email/resend-verification', { email });

      // Assuming API returns success flag or 200 OK for success
      toast?.success("Verification email resent! Please check your inbox.");
      dispatch(resendVerificationEmail());
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Failed to resend verification email.";
      toast?.error(errorMsg);
      dispatch(resendVerificationEmail(errorMsg));
    }
  };
};


export function putCartItem(itemId, payload, toast) {
    return async (dispatch) => {
        try {
            dispatch(startLoading());
            const response = await axios.put(`/cart/update-item/${itemId}`, payload, { headers });
            console.log("responseput", response);

            if (response?.data?.status === true) {
                const cart_id = localStorage?.getItem('cart_id') || null;
                const customer_id = JSON?.parse(localStorage?.getItem('user'))?.id || null;
                const token = localStorage?.getItem('accessToken') || null;

                let getPayload = {};
                // Set payload based on whether the user is authenticated
                if (token) {
                    getPayload = { status: true };
                    dispatch(getAllCartItems(customer_id, getPayload)); // Fetch cart for authenticated user
                } else {
                    getPayload = { status: false };
                    dispatch(getAllCartItems(cart_id, getPayload)); // Fetch cart for guest user
                }
            } else {
                toast.error("Failed to update the cart. Please try again.");
            }
        } catch (error) {
            console.error("Error updating cart item:", error);
            toast.error("Error updating cart item. Please try again.");
            dispatch(hasError(error.message || "Error updating cart item"));
        }
    };
}

export function deleteCartItem(itemId, toast) {
  return async (dispatch) => {
    try {
      dispatch(startLoading());

      const response = await axios.delete(`/cart/remove-item/${itemId}`, { headers });
      dispatch(deleteCartSuccess(response?.data?.status));

      if(response?.data?.status === true) {
        const productId = response?.data?.data?.item_id;

        const cartQty = JSON.parse(localStorage.getItem("cart_qty")) || {};
        delete cartQty[itemId];
        localStorage.setItem("cart_qty", JSON.stringify(cartQty));

        if(productId) {
          const cartQuantities = JSON.parse(localStorage.getItem("cart_quantities")) || {};
          delete cartQuantities[productId];
          localStorage.setItem("cart_quantities", JSON.stringify(cartQuantities));

          const cartItemIds = JSON.parse(localStorage.getItem("cart_item_ids")) || {};
          delete cartItemIds[productId];
          localStorage.setItem("cart_item_ids", JSON.stringify(cartItemIds));
        }

        const customer_id = JSON.parse(localStorage.getItem('user'))?.id;
        const cart_id = localStorage.getItem('cart_id');
        const token = localStorage.getItem('accessToken');

        dispatch(getAllCartItems(token ? customer_id : cart_id, { status: !!token }));
        toast.success("Item deleted successfully!");
      } else {
        toast.error("Verify the item and try deleting it again.");
      }
    } catch (error) {
      toast.error("Verify the item and try deleting it again.");
      dispatch(hasError(error.message || "Error deleting cart item"));
    }
  };
}

export function PostAddAllSalectItems(payload, cart_id, toast) {
    return async (dispatch) => {
        try {
            dispatch(startLoading());
            const response = await axios.post(`/cart/items/select/${cart_id}`, payload);
            if (response?.data?.status === true) {
                const cart_id = localStorage?.getItem('cart_id') || null;
                const customer_id = JSON?.parse(localStorage?.getItem('user'))?.id || null;
                const token = localStorage?.getItem('accessToken') || null;
                if (token) {
                    const payload = {
                        status: true,
                    };
                    dispatch(getAllCartItems(customer_id, payload));
                } else {
                    const payload = {
                        status: false,
                    };
                    dispatch(getAllCartItems(cart_id, payload));
                }
            } else {
                toast.error("Address Linking Failed");
            }
        } catch (error) {
            console.error("Address Linking Failed", error);
            toast.error("Address Linking Failed");
            dispatch(hasError(error.message || "Error linking address"));
        }
    };
}

// export function RemoveAllSalectItems(cart_id, toast) {
//     return async (dispatch) => {
//         try {
//             dispatch(startLoading());
//             const response = await axios.post(`/cart/items/select/${cart_id}`);
//             if (response?.data?.status === true) {
//                 const cart_id = localStorage?.getItem('cart_id') || null;
//                 const customer_id = JSON?.parse(localStorage?.getItem('user'))?.id || null;
//                 const token = localStorage?.getItem('accessToken') || null;
//                 if (token) {
//                     const payload = {
//                         status: true,
//                     };
//                     dispatch(getAllCartItems(customer_id, payload));
//                 } else {
//                     const payload = {
//                         status: false,
//                     };
//                     dispatch(getAllCartItems(cart_id, payload));
//                 }
//             } else {
//                 toast.error("Address Linking Failed");
//             }
//         } catch (error) {
//             console.error("Address Linking Failed", error);
//             toast.error("Address Linking Failed");
//             dispatch(hasError(error.message || "Error linking address"));
//         }
//     };
// }


export function PostAddSalectItems(payload, itemId, toast) {
    return async (dispatch) => {
        try {

            dispatch(startLoading());
            console.log("payload", payload, itemId,);
            const response = await axios.post(`/cart/item/select/${itemId}`, payload);
            if (response?.data?.status === true) {
                const cart_id = localStorage?.getItem('cart_id') || null;
                const customer_id = JSON?.parse(localStorage?.getItem('user'))?.id || null;
                const token = localStorage?.getItem('accessToken') || null;
                if (token) {
                    const payload = {
                        status: true,
                    };
                    dispatch(getAllCartItems(customer_id, payload));
                } else {
                    const payload = {
                        status: false,
                    };
                    dispatch(getAllCartItems(cart_id, payload));
                }
            } else {
                toast.error("Address Linking Failed");
            }
        } catch (error) {
            console.error("Address Linking Failed", error);
            toast.error("Address Linking Failed");
            dispatch(hasError(error.message || "Error linking address"));
        }
    };
}

export function RemoveAllCartItems(cart_id, toast) {
  return async (dispatch) => {
    try {
      dispatch(startLoading());
      const response = await axios.delete(`/cart/items/remove/${cart_id}`);

      if(response?.data?.status === true) {
        ['cart_id', 'cart_quantities', 'cart_item_ids', 'cart_qty'].forEach(item => localStorage.removeItem(item));

        const customer_id = JSON.parse(localStorage.getItem('user'))?.id || null;
        const token = localStorage.getItem('accessToken');

        const payload = {
          status: token ? true : false,
        };
        dispatch(getAllCartItems(token ? customer_id : cart_id, payload));
      } else {
        toast.error("Address Linking Failed");
      }
    } catch (error) {
      dispatch(hasError(error.message || "Error deleting cart items"));
    }
  };
}

export const sendNotifyRequest = (payload, toast) => {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const response = await axios.post("/notify-request", payload);
      if(response.data.success) {
        toast?.success(response.data.message || "Notification request submitted successfully!");
      } else {
        dispatch(hasError(response.data.message || "Notification failed!"));
      }
    } catch (error) {
       if(error.response.status === 409) {
          toast?.error("You are already subscribed.");
      } else {
        dispatch(hasError("Network error!"));
      }
    }
  };
};